import { apiUrl } from "../../../config/api";

// Helper to prepare headers from local storage
function getSessionHeaders() {
  const sessionData = JSON.parse(localStorage.getItem("sessionData"));
  if (!sessionData || !sessionData.sessionKey || !sessionData.username) {
    throw new Error("Session data missing");
  }

  return {
    Authorization: sessionData.sessionKey,
    Username: sessionData.username,
  };
}

function appendDateRange(params, range) {
  if (!range) return;
  if (typeof range === "string") {
    params.append("date", range);
    return;
  }
  if (range.start) params.append("dateFrom", range.start);
  if (range.end) params.append("dateTo", range.end);
}

export async function fetchUserRole(signal) {
  const headers = getSessionHeaders();

  const response = await fetch(apiUrl("/user/role"), {
    method: "GET",
    headers,
    signal,
  });

  const result = await response.json();
  if (!response.ok || !result.success) {
    throw new Error(result.message || "Failed to fetch user role");
  }

  return result.role;
}

export async function fetchAllUserDailySummary(locationId = null, dateRange = null, signal) {
  const headers = getSessionHeaders();
  const params = new URLSearchParams();
  if (locationId && locationId !== "all") params.append("location", locationId);
  appendDateRange(params, dateRange);

  const url = apiUrl(`/sales/summary/today/all${params.toString() ? `?${params.toString()}` : ""}`);

  const response = await fetch(url, {
    method: "GET",
    headers,
    signal,
  });

  const result = await response.json();
  if (!response.ok || !result.success) {
    throw new Error(result.message || "Failed to fetch all users sales summary");
  }

  return result.summary;
}

export async function fetchUserDailySummary(dateRange = null, signal) {
  const headers = getSessionHeaders();
  const sessionData = JSON.parse(localStorage.getItem("sessionData"));

  const params = new URLSearchParams();
  appendDateRange(params, dateRange);

  const url = apiUrl(`/sales/summary/today/${sessionData.username}${params.toString() ? `?${params.toString()}` : ""}`);

  const response = await fetch(url, {
    method: "GET",
    headers,
    signal,
  });

  const result = await response.json();
  if (!response.ok || !result.success) {
    throw new Error(result.message || "Failed to fetch user sales summary");
  }

  return result.summary;
}

// ✅ Fetch full user list if role is admin/superadmin, or only self otherwise
export async function fetchUserList(signal) {
  const headers = getSessionHeaders();

  const response = await fetch(apiUrl("/user/list"), {
    method: "GET",
    headers,
    signal,
  });

  const result = await response.json();
  if (!response.ok || !result.success) {
    throw new Error(result.message || "Failed to fetch user list");
  }

  return result.users; // Array of { code, username, name, role }
}

// ✅ Fetch daily sales summary for a specific username
export async function fetchDailySummaryByUsername(username, locationId = null, dateRange = null, signal) {
  const headers = getSessionHeaders();
  const params = new URLSearchParams();
  if (locationId && locationId !== "all") params.append("location", locationId);
  appendDateRange(params, dateRange);

  const url = apiUrl(`/sales/summary/today/${username}${params.toString() ? `?${params.toString()}` : ""}`);

  const response = await fetch(url, {
    method: "GET",
    headers,
    signal,
  });

  const result = await response.json();
  if (!response.ok || !result.success) {
    throw new Error(result.message || `Failed to fetch summary for ${username}`);
  }

  return result.summary;
}

// ✅ Fetch all available locations from the server
export async function fetchAllLocations(signal) {
  const headers = getSessionHeaders();

  const response = await fetch(apiUrl("/locations/all?excludeEcommerce=true"), {
    method: "GET",
    headers,
    signal,
  });

  const result = await response.json();
  if (!response.ok || !result.success) {
    throw new Error(result.message || "Failed to fetch locations");
  }

  return result.locations; // Array of { id, name }
}
// ✅ Fetch sales ranking data based on time base, interval, location, and filter
export async function fetchSalesRanking(timeBase = "weekday", interval = 30, locationId = null, timeFilter = null, signal) {
  const headers = getSessionHeaders();
  const params = new URLSearchParams();

  if (locationId && locationId !== "all") {
    params.append("location", locationId);
  }

  if (timeFilter && timeFilter !== "all") {
    params.append("filter", timeFilter);
  }

  let url;
  if (timeBase === "hourly") {
    url = apiUrl("/sales/rankings/hourly");
  } else {
    url = apiUrl(`/sales/rankings/${timeBase}/${interval}`);
  }

  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  const response = await fetch(url, { method: "GET", headers, signal });
  const result = await response.json();

  if (!response.ok || !result.success) {
    throw new Error(result.message || "Failed to fetch rankings");
  }

  return result.data;
}

export async function fetchInventoryDashboard(locationId = null, signal) {
  const headers = getSessionHeaders();
  const params = new URLSearchParams();
  if (locationId && locationId !== "all") params.append("location", locationId);

  const suffix = params.toString() ? `?${params.toString()}` : "";
  const response = await fetch(apiUrl(`/products/dashboard/inventory${suffix}`), {
    method: "GET",
    headers,
    signal,
  });
  const result = await response.json();
  if (!response.ok || !result.success) {
    throw new Error(result.message || "No se pudo cargar el inventario");
  }
  return result;
}

export async function fetchSalesByBrand(dateRange, signal) {
  const headers = getSessionHeaders();
  const params = new URLSearchParams();
  appendDateRange(params, dateRange);
  const response = await fetch(apiUrl(`/dashboard/sales-by-brand?${params.toString()}`), {
    method: "GET",
    headers,
    signal,
  });
  const result = await response.json();
  if (!response.ok || !result.success) {
    throw new Error(result.message || "No se pudo cargar el desglose por marca");
  }
  return result;
}

export async function fetchHourlyWeekdayAverage(dateRange, locationId = "all", signal) {
  const headers = getSessionHeaders();
  const params = new URLSearchParams();
  appendDateRange(params, dateRange);
  if (locationId && locationId !== "all") params.append("location", locationId);
  const response = await fetch(apiUrl(`/dashboard/hourly-weekday?${params.toString()}`), {
    method: "GET",
    headers,
    signal,
  });
  const result = await response.json();
  if (!response.ok || !result.success) {
    throw new Error(result.message || "No se pudo cargar el promedio por hora");
  }
  return result;
}

export async function fetchDashboardProductOptions(locationId = "all", signal) {
  const headers = getSessionHeaders();
  const params = new URLSearchParams();
  if (locationId && locationId !== "all") params.append("location", locationId);
  const suffix = params.toString() ? `?${params.toString()}` : "";
  const response = await fetch(apiUrl(`/dashboard/product-options${suffix}`), {
    method: "GET",
    headers,
    signal,
  });
  const result = await response.json();
  if (!response.ok || !result.success) {
    throw new Error(result.message || "No se pudo cargar la lista de productos");
  }
  return result.products || [];
}

export async function fetchProductHourlyWeekdayAverage(
  dateRange,
  productCode,
  locationId = "all",
  signal
) {
  const headers = getSessionHeaders();
  const params = new URLSearchParams({ product: productCode });
  appendDateRange(params, dateRange);
  if (locationId && locationId !== "all") params.append("location", locationId);
  const response = await fetch(apiUrl(`/dashboard/product-hourly-weekday?${params.toString()}`), {
    method: "GET",
    headers,
    signal,
  });
  const result = await response.json();
  if (!response.ok || !result.success) {
    throw new Error(result.message || "No se pudo cargar el promedio del producto");
  }
  return result;
}

export async function fetchProductMonthlySalesAverage(
  productCode,
  locationId = "all",
  signal
) {
  const headers = getSessionHeaders();
  const params = new URLSearchParams({ months: "12", product: productCode });
  if (locationId && locationId !== "all") params.append("location", locationId);
  const response = await fetch(apiUrl(`/dashboard/product-monthly-average?${params.toString()}`), {
    method: "GET",
    headers,
    signal,
  });
  const result = await response.json();
  if (!response.ok || !result.success) {
    throw new Error(result.message || "No se pudo cargar el promedio mensual del producto");
  }
  return result;
}

export async function fetchMonthlySalesAverage(locationId = "all", signal) {
  const headers = getSessionHeaders();
  const params = new URLSearchParams({ months: "12" });
  if (locationId && locationId !== "all") params.append("location", locationId);
  const response = await fetch(apiUrl(`/dashboard/monthly-average?${params.toString()}`), {
    method: "GET",
    headers,
    signal,
  });
  const result = await response.json();
  if (!response.ok || !result.success) {
    throw new Error(result.message || "No se pudo cargar el promedio mensual");
  }
  return result;
}

export async function fetchKakaoSalesByCategory(dateRange, locationId = "all", signal) {
  const headers = getSessionHeaders();
  const params = new URLSearchParams();
  appendDateRange(params, dateRange);
  if (locationId && locationId !== "all") params.append("location", locationId);
  const response = await fetch(apiUrl(`/dashboard/sales-by-category?${params.toString()}`), {
    method: "GET",
    headers,
    signal,
  });
  const result = await response.json();
  if (!response.ok || !result.success) {
    throw new Error(result.message || "No se pudo cargar las categorías");
  }
  return result;
}
